import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { DetailResponse } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  details: DetailResponse[];
  title: string;
  /** When true, full detail history is being loaded (e.g. after list used details_limit=1). */
  loading?: boolean;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({
  open,
  onClose,
  details,
  title,
  loading = false
}) => {
  // Sort details by detail_id in descending order (newest first)
  const sortedDetails = [...details].sort((a, b) => b.detail_id - a.detail_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit History - {title}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sortedDetails.length === 0 ? (
          <Typography variant="body1" sx={{ p: 2 }}>No history records found.</Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            {sortedDetails.map((detail, index) => (
              <Accordion key={detail.detail_id}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`detail-${detail.detail_id}-content`}
                  id={`detail-${detail.detail_id}-header`}
                  sx={{
                    bgcolor: index === 0 ? 'primary.light' : 'background.paper',
                    '&:hover': { bgcolor: index === 0 ? 'primary.light' : 'action.hover' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ mr: 2 }}>
                        {`Version ${sortedDetails.length - index}`} 
                        {index === 0 && <Chip size="small" label="Latest" color="primary" sx={{ ml: 1 }} />}
                      </Typography>
                      <Chip 
                        label={detail.status} 
                        color={detail.status.toLowerCase().includes('close') || 
                               detail.status.toLowerCase().includes('resolve') || 
                               detail.status.toLowerCase().includes('mitigate') || 
                               detail.status.toLowerCase().includes('validate') || 
                               detail.status.toLowerCase().includes('fulfill') ? 
                              'success' : 'warning'} 
                        size="small" 
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(detail.created_at)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'background.paper', p: 3 }}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        Detail ID
                      </Typography>
                      <Typography variant="body1">
                        {detail.detail_id}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        Created by
                      </Typography>
                      <Typography variant="body1">
                        {detail.created_by}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        Created at
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(detail.created_at)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Content Details
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'background.default', 
                        borderRadius: 1,
                        mt: 1,
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(detail.content).map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell component="th" scope="row">
                                    {key}
                                  </TableCell>
                                  <TableCell>
                                    {value === null ? '(none)' : 
                                     typeof value === 'object' ? JSON.stringify(value) : 
                                     String(value)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryDialog;
